import { db } from "../../main";
import { filterOutUndefined } from "../../utils/Utils";
import {
  IresDeviceRequest,
  PanelRequestStatus,
  IresDeviceRequestType,
} from "../models/PanelRequest";
import { getIresDeviceCollectionName } from "../models/IresDevice";
import { IresDeviceFrameBuilder } from "@/utils/IresDeviceFrameBuilder";

export function getIresDeviceRequestsRef(serialNumber: string) {
  return db
    .collection(getIresDeviceCollectionName(serialNumber))
    .doc(serialNumber)
    .collection("requests");
}

export function getIresDeviceRequestRef(
  serialNumber: string,
  commandId: string
) {
  return getIresDeviceRequestsRef(serialNumber).doc(commandId);
}

export async function savePanelRequest(panelRequest: IresDeviceRequest) {
  return getIresDeviceRequestRef(
    panelRequest.panelId,
    panelRequest.requestId
  ).set(filterOutUndefined(panelRequest));
}

export function getStartRequestId(serialNumber: string) {
  return `${serialNumber}_START_PROGRAM`;
}

export function getStopRequestId(serialNumber: string) {
  return `${serialNumber}_STOP_PROGRAM`;
}

export function getChangeProgramRequestId(serialNumber: string) {
  return `${serialNumber}_CHANGE_PROGRAM`;
}

export function getRestartPanelRequestId(serialNumber: string) {
  return `${serialNumber}_RESTART`;
}

export function getLoadRunsRequestId(serialNumber: string) {
  return `${serialNumber}_FORCE_LOAD_RUNS`;
}

export async function clearQueuedRequest(
  serialNumber: string,
  requestId: string
) {
  try {
    await getIresDeviceRequestRef(serialNumber, requestId).update({
      requestStatus: PanelRequestStatus.DONE,
    });
  } catch (error) {
    //This fails when there is nothing to update
  }
}

async function clearQueuedPanelActionRequests(serialNumber: string) {
  return Promise.all(
    [
      getStopRequestId(serialNumber),
      getStartRequestId(serialNumber),
      getChangeProgramRequestId(serialNumber),
      getRestartPanelRequestId(serialNumber),
    ].map((requestId) => clearQueuedRequest(serialNumber, requestId))
  );
}

export async function createPanelActionRequest(
  serialNumber: string,
  payload: string,
  forwardImmediately: boolean,
  requestType: IresDeviceRequestType
) {
  const now = new Date();
  let requestId = "";

  switch (requestType) {
    case IresDeviceRequestType.START_PROGRAM:
      requestId = getStartRequestId(serialNumber);
      break;
    case IresDeviceRequestType.STOP_PROGRAM:
      requestId = getStopRequestId(serialNumber);
      break;
    case IresDeviceRequestType.SELECT_PROGRAM:
      requestId = getChangeProgramRequestId(serialNumber);
      break;
    case IresDeviceRequestType.RESTART:
      requestId = getRestartPanelRequestId(serialNumber);
      break;
    case IresDeviceRequestType.LOAD_RUNS:
      requestId = getLoadRunsRequestId(serialNumber);
      break;
    default:
      return;
  }

  await clearQueuedPanelActionRequests(serialNumber);

  return savePanelRequest({
    requestId: requestId,
    priority: 500,
    canBeObserved: true,
    panelId: serialNumber,
    payload: payload,
    requestStatus: PanelRequestStatus.PENDING,
    forwardImmediately: forwardImmediately,
    createdAt: now,
    requestType: requestType,
  });
}

export function observeQueuedRequests(
  serialNumber: string,
  onNext: (queuedPanelRequests: Array<IresDeviceRequest>) => void
) {
  return getIresDeviceRequestsRef(serialNumber)
    .where("canBeObserved", "==", true)
    .orderBy("createdAt", "desc")
    .limit(1)
    .onSnapshot((snapshot) => {
      onNext(
        snapshot.docs
          .map((data) => data.data() as IresDeviceRequest)
          .filter(
            (panelRequest) =>
              panelRequest.requestStatus !== PanelRequestStatus.DONE
          )
      );
    });
}

export function createRestartPanelCommand(serialNumber: string) {
  const builder = new IresDeviceFrameBuilder(35);
  const frame = builder.toIresDeviceFrame();
  const requestType = IresDeviceRequestType.RESTART;

  return createPanelActionRequest(serialNumber, frame, true, requestType);
}
